import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ClipboardList, Plus } from 'lucide-react';
import RecordReadingsModal from './RecordReadingsModal';
import ReadingChips, { ReadingItems } from './ReadingChips';
import useCheckAccess from './useCheckAccess';
import { fetchVisitReadings } from '../../redux/checkSetSlice';
import { offeredSets, isVisitOpenForStaff, hhmm } from '../../utils/checkSets';
import { isPlanRefusal } from '../../utils/planRefusal';
import { serverReason } from '../patientTabs/prescriptionHelpers';

// The readings of ONE visit, as the desk sees them in the appointment detail
// (and a staff member or the admin in a patient's visit history): a chip per
// live reading, the values, and "Record readings".
//
// Shown only to a recorder (the visit's own doctor, the admin, staff with
// `checks.record`) of a hospital that has check sets — the readings endpoint
// refuses everybody else, and a refusal here is not the desk's problem to
// read, so it simply draws nothing.
function VisitReadingsPanel({ appointment, className = '' }) {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const access = useCheckAccess();
    const myId = typeof localStorage !== 'undefined' ? localStorage.getItem('ID') : null;
    const appointmentId = appointment?.appointmentId || null;
    const isDoctor = access.role === 'doctor';
    // NOT gated on `clinicalHidden`: the desk's rows arrive with the clinical
    // record emptied (staff are not clinicians), and readings are not that
    // record — the readings endpoint has its own rule (checks.record, this
    // visit only). Nor on the status: a visit's readings are shown on every
    // status, a cancelled one included when it has any (owner, 26 Sep 2026);
    // only recording stops (`canOpen`).
    const eligible = Boolean(access.canRecord && appointmentId
        && (!isDoctor || String(appointment.doctorId) === String(myId)));
    const visit = useSelector((state) => (appointmentId ? state.checkSets?.visits?.[appointmentId] : null));
    const [recording, setRecording] = useState(false);
    const [initialSetId, setInitialSetId] = useState(null);

    useEffect(() => {
        if (eligible) dispatch(fetchVisitReadings({ appointmentId, force: true }));
    }, [eligible, appointmentId, dispatch]);

    if (!eligible) return null;
    const data = visit?.data;
    // 1 Oct 2026 (owner): check_sets is the VISIT's doctor's plan control.
    // The desk recording on a doctor whose plan lacks it is refused with
    // PLAN_FEATURE_MISSING — that is the one refusal worth saying here (any
    // other still draws nothing, as before).
    if (!data && visit?.error && isPlanRefusal(visit.error)) {
        return (
            <p data-testid="visit-readings-plan-refused" className={`text-xs text-gray-500 ${className}`}>
                {serverReason(visit.error, t('checkSets.readings_load_failed'), i18n?.language || 'en')}
            </p>
        );
    }
    if (!data) return null;

    const readings = data.readings || [];
    const sets = offeredSets(data);
    const status = data.appointmentStatus || appointment.status;
    // ONE open-status rule for every role: Record / Correct only while the
    // visit is open (not Completed, NoShow or Cancelled). A doctor sees a
    // closed visit's readings read-only here like anybody else, and corrects
    // a Completed visit through Medical history's Edit — as the Pro-App does.
    const canOpen = sets.length > 0 && isVisitOpenForStaff(status)
        && (!isDoctor || data.enabled !== false);
    if (readings.length === 0 && !canOpen) return null;

    const open = (setId = null) => {
        setInitialSetId(setId);
        setRecording(true);
    };

    return (
        <section data-testid="visit-readings-panel" className={`rounded-2xl border border-[#4ABDBD]/25 bg-gradient-to-br from-[#4ABDBD]/5 to-white p-4 ${className}`}>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <h3 className="flex items-center gap-2 text-sm font-bold text-[#2D8A8A]">
                    <ClipboardList className="h-4 w-4" aria-hidden="true" />
                    {t('checkSets.block_title')}
                </h3>
                {canOpen && (
                    <button
                        type="button"
                        data-testid="visit-readings-record"
                        onClick={() => open(null)}
                        className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] px-3 py-1.5 text-xs font-semibold text-white shadow transition-all hover:shadow-md"
                    >
                        <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                        {t('checkSets.record_readings')}
                    </button>
                )}
            </div>
            {readings.length === 0 ? (
                <p data-testid="visit-readings-empty" className="text-xs text-gray-500">{t('checkSets.no_readings_yet')}</p>
            ) : (
                <>
                    <ReadingChips readings={readings} testId="visit-readings-chips" className="mb-3" />
                    <ul className="flex flex-col gap-2">
                        {readings.map((r) => (
                            <li key={r.readingId} data-testid={`visit-readings-reading-${r.readingId}`} className="rounded-xl border border-gray-100 bg-white p-3">
                                <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                                    <p className="text-sm font-semibold text-gray-800">{r.setName}</p>
                                    <div className="flex items-center gap-2">
                                        <span className="text-[11px] text-gray-500">
                                            {t('checkSets.recorded_by', { name: r.recordedByName || t(`checkSets.role_${r.recordedRole || 'staff'}`), time: hhmm(r.recordedAt) })}
                                        </span>
                                        {canOpen && sets.some((s) => s.setId === r.setId) && (
                                            <button
                                                type="button"
                                                data-testid={`visit-readings-correct-${r.readingId}`}
                                                onClick={() => open(r.setId)}
                                                className="rounded-lg px-2 py-0.5 text-[11px] font-semibold text-[#2D8A8A] hover:bg-[#4ABDBD]/10"
                                            >
                                                {t('checkSets.correct')}
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <ReadingItems items={r.items} dense />
                            </li>
                        ))}
                    </ul>
                </>
            )}
            <RecordReadingsModal
                isOpen={recording}
                appointment={{ ...appointment, patientName: [appointment.patientName, appointment.patientSurname].filter(Boolean).join(' ') || appointment.patientName }}
                initialSetId={initialSetId}
                onClose={() => setRecording(false)}
            />
        </section>
    );
}

export default VisitReadingsPanel;
